import { useState, useMemo, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Plus, Package, Loader2, AlertCircle, Users } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CriarItemPedidoModal } from '../modals/CriarItemPedidoModal';
import { BotaoRevisarProposta } from '../BotaoRevisarProposta';
import { useNavigate } from 'react-router-dom';
import { EditarItemCompletoModal } from '../modals/EditarItemCompletoModal';
import { CancelarItemModal } from '../CancelarItemModal';
import { ItemDetalhadoInline } from '../inline/ItemDetalhadoInline';
import { useUpdateItemPedido } from '@/hooks/useUpdateItemPedido';
import { useHasAnyRole, useHasRole } from '@/hooks/useHasRole';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { BotaoFecharPedido } from '@/components/pedido/BotaoFecharPedido';
import { BotaoAcaoBKO, BotoesAcaoBKOList } from '@/components/pedido/BotaoAcaoBKO';
import { useItemGestorCessionario } from '@/hooks/useItemGestorCessionario';
import { usePainelPedido } from '@/contexts/PainelPedidoContext';

interface ItensTabProps {
  pedidoId: string;
  pedido: any;
  focusItemSeq?: number;
}

// Wrapper para passar todosItens para ItemDetalhadoInline
function ItemDetalhadoWrapper({ item, todosItens, ...props }: any) {
  return <ItemDetalhadoInline item={item} todosItens={todosItens} {...props} />;
}

export default function ItensTab({ pedidoId, pedido, focusItemSeq }: ItensTabProps) {
  const navigate = useNavigate();
  const { close: closePainelPedido } = usePainelPedido();
  const [modalCriarOpen, setModalCriarOpen] = useState(false);
  const [modalEditarOpen, setModalEditarOpen] = useState(false);
  const [modalCancelarOpen, setModalCancelarOpen] = useState(false);
  const [itemParaEditar, setItemParaEditar] = useState<any>(null);
  const [itemParaCancelar, setItemParaCancelar] = useState<any>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const updateItemMutation = useUpdateItemPedido();
  const isGestor = useHasAnyRole(['master', 'diretor', 'gerente', 'coordenador', 'supervisor']);
  const isMaster = useHasRole('master'); //  SOMENTE master pode deletar permanentemente
  const [aplicandoGestor, setAplicandoGestor] = useState(false);

  // ── REALTIME (Douglas 07/08): o recover roda no modal do LEAD e o painel
  // ficava CANCELADO até o F5 — invalidation cross-tela é frágil (id da mãe ×
  // filho, bundle velho no clique de quem mudou). `itens_pedido` está no
  // bridge do realtime do backend: QUALQUER update de item da família (mãe ou
  // filho, por RPC, BKO, kanban ou outra pessoa) invalida a lista aqui,
  // na hora. Filtro client-side: o evento chega para a tabela toda.
  const idsFamiliaRef = useRef<Set<string>>(new Set());
  const itemIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!pedidoId) return;
    const canal = supabase
      .channel(`itens-pedido-360-${pedidoId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'itens_pedido' }, (payload: { new?: { id?: string; pedido_id?: string } | null; old?: { id?: string; pedido_id?: string } | null }) => {
        const row = payload?.new ?? payload?.old;
        if (!row) return;
        const daFamilia = row.pedido_id === pedidoId
          || idsFamiliaRef.current.has(row.pedido_id)
          || itemIdsRef.current.has(row.id);
        if (!daFamilia) return;
        queryClient.invalidateQueries({ queryKey: ['pedido-itens-completo', pedidoId] });
        queryClient.invalidateQueries({ queryKey: ['cancelamentos-pedido', pedidoId] });
      })
      .subscribe();
    return () => { supabase.removeChannel(canal); };
  }, [pedidoId, queryClient]);

  // ✅ CORREÇÃO: Buscar itens ANTES de qualquer return condicional (regra de hooks)
  const { data: itens = [], isLoading: isLoadingItens, refetch } = useQuery({
    queryKey: ['pedido-itens-completo', pedidoId],
    queryFn: async () => {
      const { data: filhos } = await supabase
        .from('pedidos')
        .select('id')
        .eq('pedido_mae_id', pedidoId)
        .eq('deletado', false);
      const pedidosIds = [pedidoId, ...(filhos?.map(f => f.id) || [])];
      idsFamiliaRef.current = new Set(pedidosIds);
      
      // 🎯 3. SO os campos que a VIEW tem (status_cor/franquia_gb/endereco_instalacao/gestor_id/
      // cessionario_id/data_cancelamento/item_updated_at NAO existem na view -> antes a query
      // inteira dava erro e o painel ficava VAZIO; vem da tabela base abaixo). (Douglas 16/06)
      const { data, error } = await supabase
        .from('v_planilha_mae')
        .select([
          'item_id', 'pedido_id', 'item_seq', 'numero_pedido', 'numero_pedido_completo',
          'status_nome', 'quadro_atual', 'coluna_atual',
          'produto_id', 'produto', 'item_familia', 'faturamento_tipo',
          'quantidade', 'valor_unit', 'subtotal',
          'ddd', 'linha', 'operadora_doadora', 'plano_nome', 'plano_valor',
          'linhas_telefone', 'gb',
          'data_ativacao', 'item_created_at',
        ].join(','))
        .in('pedido_id', pedidosIds)
        .order('item_seq');
      
      if (error) throw error;

      const { data: baseRows, error: baseRowsError } = await supabase
        .from('itens_pedido')
        // observacoes_item entra aqui porque e' nela que o Passaport guarda de
        // QUAL LINHA ele e' ("Vinculado à linha: ..."). Sem o campo, o roaming
        // nunca aninhava e continuava como cartao solto. (Douglas 01/09/2026)
        .select('id, franquia_gb, endereco_instalacao, gestor_id, cessionario_id, data_cancelamento, updated_at, observacoes_item, linha_id, linha_pai_id')
        .in('pedido_id', pedidosIds);
      if (baseRowsError) throw baseRowsError;
      // ✅ Helper robusto para converter valores Proxy/object para string de forma segura.
      // Precisa normalizar TAMBÉM a chave do mapa da tabela base: o gateway pode
      // devolver o UUID como { value }, e String(objeto) vira "[object Object]".
      const toSafeString = (val: any): string => {
          if (val === null || val === undefined) return '';
          if (typeof val === 'string') return val.trim();
          if (typeof val === 'object' && val !== null && 'value' in val) {
            return String(val.value || '').trim();
          }
          try {
            const serialized = JSON.parse(JSON.stringify(val));
            if (serialized === null || serialized === undefined) return '';
            if (typeof serialized === 'string') return serialized.trim();
            if (typeof serialized === 'object' && 'value' in serialized) {
              return String(serialized.value || '').trim();
            }
            const str = String(serialized).trim();
            if (str === '[object Object]') return '';
            return str;
          } catch (error) {
            console.warn('⚠️ toSafeString failed for:', val, error);
            return '';
          }
      };
      const __baseMap = new Map(((baseRows as any) || []).map((b) => [toSafeString(b.id), b]));

      // ✅ Processar campos que podem vir como proxy/objeto da view
      return (data || []).map((item: any) => {

        const __base = __baseMap.get(toSafeString(item.item_id)) || {};
        return {
          id: toSafeString(item.item_id),
          pedido_id: toSafeString(item.pedido_id),
          item_seq: item.item_seq,
          numero_pedido: item.numero_pedido,
          numero_pedido_completo: toSafeString(item.numero_pedido_completo),
          status_nome: toSafeString(item.status_nome),
          status_cor: toSafeString(item.status_cor),
          quadro_atual: toSafeString(item.quadro_atual) || String(item.quadro_atual || '').trim(),
          coluna_atual: toSafeString(item.coluna_atual) || String(item.coluna_atual || '').trim(),
          produto_id: item.produto_id,
          produto_nome: toSafeString(item.produto),
          familia: toSafeString(item.item_familia),
          faturamento_tipo: item.faturamento_tipo,
          quantidade: item.quantidade || 1,
          valor_unit: item.valor_unit || 0,
          subtotal: item.subtotal || 0,
          ddd: toSafeString(item.ddd),
          linha: toSafeString(item.linha),
          operadora_doadora: toSafeString(item.operadora_doadora),
          plano_nome: toSafeString(item.plano_nome),
          plano_valor: item.plano_valor,
          franquia_gb: (__base as any).franquia_gb,
          endereco_instalacao: (__base as any).endereco_instalacao,
          observacoes_item: (__base as { observacoes_item?: string | null }).observacoes_item,
          linhas_telefone: item.linhas_telefone,
          linha_id: toSafeString((__base as { linha_id?: string | null }).linha_id),
          linha_pai_id: toSafeString((__base as { linha_pai_id?: string | null }).linha_pai_id),
          gestor_id: (__base as any).gestor_id,
          cessionario_id: (__base as any).cessionario_id,
          produto: { nome: toSafeString(item.produto) || 'Produto não identificado' },
          data_ativacao: item.data_ativacao,
          data_cancelamento: (__base as any).data_cancelamento,
          created_at: item.item_created_at,
          updated_at: (__base as any).updated_at,
        };
      });
    },
    enabled: !!pedidoId,
    staleTime: 60_000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    // 'always' (Douglas 07/08): o painel de pedido é tela operacional — abrir
    // TEM que mostrar o agora. Com `true` + staleTime, reabrir em <60s pintava
    // cache velho: o consultor recuperava o item no modal do lead, abria o
    // pedido e via o estado ANTIGO (o global ainda é refetchOnMount:false).
    refetchOnMount: 'always',
  });
  useEffect(() => {
    itemIdsRef.current = new Set((itens || []).map((i: { id: string }) => i.id));
  }, [itens]);

  // ── O QUE ACOMPANHA A LINHA (Douglas 01/09/2026) ────────────────────────────
  // Passaport (roaming) e serviço digital são itens próprios — precisam ser, é
  // deles que sai o faturamento e é neles que o BKO trabalha. Mas na tela eles
  // apareciam como cartões soltos no fim da lista: com 5 linhas, 2 Passaports e
  // 3 licenças o consultor via 10 cartões iguais e só descobria de quem era cada
  // um abrindo a observação. Agora cada um aparece recuado DENTRO do cartão da
  // linha a que pertence. Nada muda no banco: mesmo item, mesmo status, mesma
  // esteira — muda só onde ele é desenhado.
  const { itensPrincipais, acompanhamentosPorItem } = useMemo(() => {
    type ItemVinculo = {
      id: string;
      observacoes_item?: string | null;
      familia?: string | null;
      produto_nome?: string | null;
      ddd?: string | null;
      linha?: string | null;
      linhas_telefone?: unknown;
      linha_id?: string | null;
      linha_pai_id?: string | null;
    };
    const lista = (itens || []) as ItemVinculo[];
    const soDigitos = (v: unknown) => String(v ?? '').replace(/\D/g, '');
    // Telefones que um item chama de seus (a linha própria dele).
    const telefonesDoItem = (it: ItemVinculo): string[] => {
      const arr = Array.isArray(it?.linhas_telefone)
        ? (it.linhas_telefone as Array<{ ddd?: string; linha?: string }>)
        : [];
      const doJson = arr.map((lt) => soDigitos(`${lt?.ddd ?? ''}${lt?.linha ?? ''}`));
      const daColuna = soDigitos(`${it?.ddd ?? ''}${it?.linha ?? ''}`);
      return [...doJson, daColuna].filter((d: string) => d.length >= 10);
    };
    // Telefone a que o item se VINCULA, quando ele não é linha:
    // Passaport guarda na observação; serviço digital guarda em ddd+linha.
    const telefoneVinculo = (it: ItemVinculo): string | null => {
      const obs = String(it?.observacoes_item ?? '');
      const m = obs.match(/Vinculado à linha:\s*([\d()\s-]+)/i);
      if (m) {
        const d = soDigitos(m[1]);
        if (d.length >= 10) return d;
      }
      const familia = String(it?.familia ?? '').toUpperCase();
      const produto = String(it?.produto_nome ?? '').toUpperCase();
      if (familia === 'TI' || produto === 'APARELHO') {
        const d = soDigitos(`${it?.ddd ?? ''}${it?.linha ?? ''}`);
        if (d.length >= 10) return d;
      }
      return null;
    };
    const casa = (a: string, b: string) => a === b || a.endsWith(b) || b.endsWith(a);

    // ⛔ Quem pode ser FILHO nunca pode ser PAI. Duas licenças no mesmo telefone,
    // sem um item de linha correspondente, viravam pai uma da outra: as duas
    // saíam de `itensPrincipais`, ficavam penduradas num id que ninguém
    // renderiza e SUMIAM da aba — item existindo no banco e invisível para o
    // BKO. (Douglas 01/09/2026)
    const candidatosAFilho = new Set(lista
      .filter((it) => telefoneVinculo(it) !== null || Boolean(it.linha_pai_id))
      .map((it) => it.id));
    const mapa = new Map<string, ItemVinculo[]>();
    const filhos = new Set<string>();
    for (const it of lista) {
      if (it.linha_pai_id) {
        const paiPorId = lista.find((p) => p.id !== it.id && p.linha_id === it.linha_pai_id);
        if (paiPorId) {
          mapa.set(paiPorId.id, [...(mapa.get(paiPorId.id) || []), it]);
          filhos.add(it.id);
          continue;
        }
      }
      const vinculo = telefoneVinculo(it);
      if (!vinculo) continue;
      const pai = lista.find(
        (p) => p.id !== it.id
          && !candidatosAFilho.has(p.id)
          && telefonesDoItem(p).some((t: string) => casa(t, vinculo)),
      );
      if (!pai) continue; // sem dono conhecido → segue como cartão de primeiro nível
      mapa.set(pai.id, [...(mapa.get(pai.id) || []), it]);
      filhos.add(it.id);
    }
    const principais = lista.filter((it) => !filhos.has(it.id));
    // Rede: filho cujo pai não está na lista de primeiro nível volta a ser card
    // próprio — nada pode simplesmente desaparecer da tela.
    const idsPrincipais = new Set(principais.map((it) => it.id));
    for (const [paiId, lista_] of [...mapa.entries()]) {
      if (idsPrincipais.has(paiId)) continue;
      mapa.delete(paiId);
      for (const orfao of lista_) {
        filhos.delete(orfao.id);
        principais.push(orfao);
      }
    }
    return {
      itensPrincipais: principais,
      acompanhamentosPorItem: mapa,
    };
  }, [itens]);

  // ✅ Verificação defensiva - agora como variável, não early return
  const isLoadingPedido = !pedido;

  const handleUpdateItem = async (itemId: string, field: string, value: any) => {
    await updateItemMutation.mutateAsync({
      itemId,
      updates: { [field]: value }
    });
  };

  const handleEditarItem = (item: any) => {
    setItemParaEditar(item);
    setModalEditarOpen(true);
  };

  const handleCancelarItem = (item: any) => {
    setItemParaCancelar(item);
    setModalCancelarOpen(true);
  };

  const handleDeletarItem = async (itemId: string) => {
    if (!confirm('Tem certeza que deseja deletar este item?')) return;

    try {
      const { error } = await supabase
        .from('itens_pedido')
        .delete()
        .eq('id', itemId);

      if (error) throw error;

      toast({
        title: '✓ Item deletado',
        description: 'Item removido com sucesso',
      });

      queryClient.invalidateQueries({ queryKey: ['pedido-itens-completo', pedidoId] });
      queryClient.invalidateQueries({ queryKey: ['pedidos'] });
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erro ao deletar',
        description: error.message,
      });
    }
  };

  // ✅ Helper robusto para extrair valor de proxy/objeto (igual ao toString)
  const extractValue = (val: any): string | null => {
    if (val === null || val === undefined) return null;
    if (typeof val === 'object' && val._type === 'undefined') return null;
    if (typeof val === 'object' && 'value' in val) {
      const innerVal = val.value;
      if (innerVal === null || innerVal === undefined || innerVal === 'undefined') return null;
      return String(innerVal).trim() || null;
    }
    const str = String(val).trim();
    if (str === '' || str === 'undefined' || str === 'null') return null;
    return str;
  };

  // ✅ Verificar se o pedido já foi fechado/processado
  const { data: pedidoFechado, isLoading: checkingPedidoFechado } = useQuery({
    queryKey: ['check-pedido-fechado-itenstab', pedidoId],
    queryFn: async () => {
      if (!pedidoId) return false;
      
      // ✅ 1. Se ESTE pedido É um FILHO → BLOQUEADO
      if (pedido?.pedido_mae_id) {
        console.log('🔒 [ItensTab] Pedido É FILHO → BLOQUEADO');
        return true;
      }
      
      // ✅ 2. Se este pedido TEM filhos COM ITENS → BLOQUEADO (filhos vazios/órfãos não bloqueiam)
      const { data: filhos, error: filhosError } = await supabase
        .from('pedidos')
        .select('id')
        .eq('pedido_mae_id', pedidoId);
      
      if (filhosError) {
        console.error('❌ Erro ao verificar pedidos filhos:', filhosError);
        return false;
      }
      
      if (filhos && filhos.length > 0) {
        const filhoIds = filhos.map(f => f.id);
        const { data: itensFilhos } = await supabase
          .from('itens_pedido')
          .select('id')
          .in('pedido_id', filhoIds)
          .limit(1);
        
        if (itensFilhos && itensFilhos.length > 0) {
          console.log('🔒 [ItensTab] Pedido TEM FILHO COM ITENS → BLOQUEADO', {
            pedidoMae: pedidoId.substring(0, 8)
          });
          return true;
        }
        // Filhos vazios → não bloquear
      }
      
      // ✅ 3. Se os itens estão em colunas pós-fechamento → BLOQUEADO
      const colunasPosFechamento = [
        'PEDIDO FECHADO',
        'PEDIDO RECUPERADO',
        'CRIAÇÃO PEDIDO',
        'ACEITE',
        'INPUT',
        'INPUT REALIZADO MOVEL',
        'INPUT REALIZADO FIXA',
        'QUALIDADE',
        'FORA SUL - CRIAR PEDIDO'
      ];
      
      const { data: itensProcessados, error: itensError } = await supabase
        .from('itens_pedido')
        .select('id')
        .eq('pedido_id', pedidoId)
        .in('coluna_atual', colunasPosFechamento)
        .limit(1);
      
      if (itensError) {
        console.error('❌ Erro ao verificar itens processados:', itensError);
        return false;
      }
      
      if (itensProcessados && itensProcessados.length > 0) {
        console.log('🔒 [ItensTab] Itens em coluna pós-fechamento → BLOQUEADO');
        return true;
      }
      
      return false;
    },
    enabled: !!pedidoId && !!pedido,
    staleTime: 5000,
    refetchOnMount: true,
  });

  // ✅ Aplicar gestor do primeiro item a todos os itens sem gestor
  const handleAplicarGestorATodos = async () => {
    const itemComGestor = itens.find((i: any) => i.gestor_id);
    if (!itemComGestor) return;
    const gestorId = itemComGestor.gestor_id;
    const itensSemGestor = itens.filter((i: any) => !i.gestor_id).map((i: any) => i.id);
    if (itensSemGestor.length === 0) {
      toast({ title: '✓ Todos os itens já têm gestor' });
      return;
    }
    setAplicandoGestor(true);
    try {
      const { error } = await supabase
        .from('itens_pedido')
        .update({ gestor_id: gestorId })
        .in('id', itensSemGestor);
      if (error) throw error;
      toast({ title: `✓ Gestor aplicado a ${itensSemGestor.length} item(ns)` });
      queryClient.invalidateQueries({ queryKey: ['pedido-itens-completo', pedidoId] });
    } catch (err: any) {
      toast({ variant: 'destructive', title: 'Erro ao aplicar gestor', description: err.message });
    } finally {
      setAplicandoGestor(false);
    }
  };

  // ✅ Verificar se há itens: algum com gestor e algum sem gestor
  const podeAplicarGestorATodos = useMemo(() => {
    if (!itens || itens.length < 2) return false;
    const temComGestor = itens.some((i: any) => i.gestor_id);
    const temSemGestor = itens.some((i: any) => !i.gestor_id);
    return temComGestor && temSemGestor;
  }, [itens]);

  // ✅ Verificar se pode criar novos itens
  const podeCriarItem = useMemo(() => {
    // ❌ (B) Bloquear se pedido veio de proposta (lead_origem_id)
    if (pedido?.lead_origem_id) {
      // Pedido de PROPOSTA: os itens normalmente vem da proposta. MAS se ele ficou VAZIO
      // (a transferencia da proposta falhou), libera adicionar manualmente — senao vira
      // pedido FANTASMA travado ("nao existe": existe, mas sem item e sem como por item).
      // (fix 25/06)
      if (!itens || itens.length === 0) {
        console.log('⚠️ [ItensTab] Pedido de PROPOSTA mas VAZIO → liberando add item (recovery)');
        return true;
      }
      console.log('🚫 [ItensTab] Pedido veio de PROPOSTA (com itens) → bloqueando + Itens');
      return false;
    }
    
    // ❌ Bloquear se pedido foi fechado (tem filho)
    if (pedidoFechado) {
      console.log('🚫 [ItensTab] Pedido FECHADO → bloqueando criação de itens');
      return false;
    }
    
    // ✅ Se não tem itens, pode criar (desde que não esteja fechado)
    if (!itens || itens.length === 0) return true;
    
    // Bloquear se qualquer item estiver:
    // 1. Em PEDIDO FECHADO (qualquer quadro)
    // 2. Fora do quadro COMERCIAL
    const todosPermitemAdicionar = itens.every(item => {
      const quadro = extractValue(item.quadro_atual) || 'COMERCIAL';
      const coluna = extractValue(item.coluna_atual);
      
      // ❌ Bloquear se estiver em PEDIDO FECHADO
      if (coluna === 'PEDIDO FECHADO') {
        console.log('🚫 [ItensTab] Item em PEDIDO FECHADO detectado:', {
          itemId: item.id.substring(0, 8),
          quadro,
          coluna
        });
        return false;
      }
      
      // ❌ Bloquear se estiver fora de COMERCIAL
      if (quadro !== 'COMERCIAL') {
        console.log('🚫 [ItensTab] Item fora de COMERCIAL detectado:', {
          itemId: item.id.substring(0, 8),
          quadro,
          coluna
        });
        return false;
      }
      
      return true;
    });
    
    console.log('🔍 [ItensTab] podeCriarItem:', {
      temItens: itens.length,
      todosPermitemAdicionar,
      detalhes: itens.map(i => ({
        id: i.id.substring(0, 8),
        quadro: extractValue(i.quadro_atual),
        coluna: extractValue(i.coluna_atual)
      }))
    });
    
    return todosPermitemAdicionar;
  }, [itens, pedidoFechado, pedido?.lead_origem_id]);

  return (
    <div className="space-y-[11px]">
      {/* CABEÇALHO */}
      <div className="space-y-2">
        <div className="flex items-start justify-between">
          {/* R5: "Itens do Pedido (N)" + "Gerencie os itens incluídos neste
              pedido" foram apagados — a ABA ativa já diz "Itens" e o contador
              vive nela. O subtítulo era instrução de manual: ocupava ~60px e
              não mudava nenhuma decisão. */}
          <div />
          <div className="flex gap-2 flex-wrap justify-end">
            {/* Aplicar Gestor a Todos */}
            {podeAplicarGestorATodos && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleAplicarGestorATodos}
                      disabled={aplicandoGestor}
                    >
                      {aplicandoGestor
                        ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        : <Users className="w-4 h-4 mr-2" />
                      }
                      Aplicar Gestor a Todos
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Copia o gestor do primeiro item para todos os itens sem gestor</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {/* Revisar Proposta - para pedidos de proposta (lead) em COMERCIAL, CANCELADO ou sem itens (bug de conversão) */}
            {(() => {
              if (!pedido?.lead_origem_id) return null;

              // Coletar quadros de TODOS os itens (resiliente a RLS parcial)
              const quadrosItens = [
                ...(itens || []).map((i: any) => i?.quadro_atual),
                ...((pedido as any)?.itens || []).map((i: any) => i?.quadro_atual),
              ].filter(Boolean) as string[];

              // Fallback: quadro do próprio pedido (quando itens vêm vazios por RLS)
              const quadroPedido = (pedido as any)?.quadro_atual as string | undefined;

              // Sem itens + tem lead = pedido criado sem itens (bug) → sempre pode revisar
              const semItensComLead = (itens?.length || 0) === 0 && !((pedido as any)?.itens?.length);

              const ehCancelado =
                quadrosItens.some((q) => q === 'PEDIDO_CANCELADO') ||
                quadroPedido === 'PEDIDO_CANCELADO';
              const ehComercial = quadrosItens.length > 0
                ? quadrosItens.every((q) => q === 'COMERCIAL')
                : quadroPedido === 'COMERCIAL';

              // Cancelado tratado pelo banner do topo (Revisar+Recuperar) pra nao duplicar. (Douglas 16/06)
              // Revisar regenera a proposta do pedido inteiro. Em pedido misto
              // (ex.: 1 item fechado e os demais em Comercial), exibir a ação
              // sugere que o item já finalizado também pode ser refeito. Só
              // libera a revisão comum quando TODOS os itens são editáveis.
              const podeRevisarPorQuadro = semItensComLead || ehComercial;
              if (!podeRevisarPorQuadro) return null;

              const quadroAtualResolvido = ehCancelado
                ? 'PEDIDO_CANCELADO'
                : ehComercial
                  ? 'COMERCIAL'
                  : (quadrosItens[0] || quadroPedido || 'COMERCIAL');
              return (
                <BotaoRevisarProposta 
                  pedido={{
                    id: pedido.id,
                    lead_origem_id: pedido.lead_origem_id,
                    quadro_atual: semItensComLead ? 'COMERCIAL' : quadroAtualResolvido,
                  }}
                  isCancelado={ehCancelado}
                  onRevisaoIniciada={(resolvedId?: string) => {
                    closePainelPedido();
                    navigate(`/comercial?tab=prospeccao&lead=${pedido.lead_origem_id}&fromPedido=${resolvedId || pedido.id}`);
                  }}
                />
              );
            })()}
            
            {/* Adicionar Item - APENAS para pedidos manuais (sem lead_origem_id) */}
            {!pedido?.lead_origem_id && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      onClick={() => setModalCriarOpen(true)} 
                      size="sm"
                      disabled={!podeCriarItem}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Adicionar Item
                    </Button>
                  </TooltipTrigger>
                  {!podeCriarItem && (
                    <TooltipContent>
                      <p>
                        {pedidoFechado 
                          ? 'Este pedido já foi fechado e não pode receber novos itens'
                          : 'Só é possível adicionar itens quando todos os itens estão no quadro COMERCIAL'
                        }
                      </p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
        
        {/* Alert informativo - só para pedidos manuais fora de COMERCIAL */}
        {!pedido?.lead_origem_id && !podeCriarItem && (
          <Alert className="py-2 px-3 bg-primary/5 dark:bg-primary/20 border-primary/40 dark:border-primary">
            <AlertCircle className="h-3.5 w-3.5 text-primary dark:text-primary" />
            <AlertDescription className="text-xs leading-relaxed text-primary dark:text-primary ml-6">
              <strong className="font-semibold">Pedido finalizado:</strong> Este pedido já saiu da fase COMERCIAL e está em tramitação. Não é possível adicionar novos itens.
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* LISTA DE ITENS */}
      <div className="space-y-2">
        {(isLoadingItens || isLoadingPedido) ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">Carregando itens...</span>
          </div>
        ) : !pedido ? (
          <Alert variant="destructive">
            <AlertDescription>
              Pedido não encontrado (ID: {pedidoId})
            </AlertDescription>
          </Alert>
        ) : itens && itens.length > 0 ? (
          <>
            {itensPrincipais.map((item: any) => {
              try {
                const acompanham = (acompanhamentosPorItem.get(item.id) || []) as Array<{ id: string }>;
                return (
                  <section
                    key={item.id}
                    className={acompanham.length > 0
                      ? 'overflow-hidden rounded-xl border border-primary/15 bg-gradient-to-b from-primary/[0.045] to-background p-2 shadow-sm'
                      : undefined}
                  >
                    <ItemDetalhadoWrapper
                      item={item}
                      pedido={pedido}
                      todosItens={itens}
                      onEditItem={!pedido?.lead_origem_id ? handleEditarItem : undefined}
                      onDeleteItem={undefined}
                      onCancelarItem={handleCancelarItem}
                      onUpdateField={handleUpdateItem}
                      isGestor={isGestor}
                    />
                    {acompanham.length > 0 && (
                      <div className="mt-2 space-y-1.5 pl-2">
                        <div className="flex items-center gap-2 px-1">
                          <span className="h-px flex-1 bg-primary/15" />
                          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.9px] text-primary">
                            {acompanham.length} {acompanham.length === 1 ? 'acompanhamento' : 'acompanhamentos'} desta linha
                          </span>
                          <span className="h-px flex-1 bg-primary/15" />
                        </div>
                        {acompanham.map((filho) => (
                          <ItemDetalhadoWrapper
                            key={filho.id}
                            item={filho}
                            pedido={pedido}
                            todosItens={itens}
                            onEditItem={!pedido?.lead_origem_id ? handleEditarItem : undefined}
                            onDeleteItem={undefined}
                            onCancelarItem={handleCancelarItem}
                            onUpdateField={handleUpdateItem}
                            isGestor={isGestor}
                            compact
                          />
                        ))}
                      </div>
                    )}
                  </section>
                );
              } catch (error) {
                console.error('❌ [ItensTab] Erro ao renderizar item:', item, error);
                return (
                  <Alert key={item.id} variant="destructive">
                    <AlertDescription>
                      Erro ao renderizar item {item.numero_pedido_completo || item.seq}
                    </AlertDescription>
                  </Alert>
                );
              }
            })}
          </>
        ) : (
          <Card className="border-2 border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Package className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">
                Nenhum item adicionado ainda
              </p>
              <Button 
                onClick={() => setModalCriarOpen(true)} 
                variant="outline"
                disabled={!podeCriarItem}
              >
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Primeiro Item
              </Button>
              {!podeCriarItem && (
                <p className="text-xs text-muted-foreground mt-2">
                  {pedidoFechado
                    ? 'Este pedido já foi fechado e não pode receber novos itens'
                    : 'Este pedido já saiu do COMERCIAL e não pode receber novos itens'
                  }
                </p>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* RESUMO */}
      {/* R7: com UM item só o total repete o valor da linha logo acima —
          duas vezes o mesmo número não informa nada. Só aparece com 2+. */}
      {itens && itens.length > 1 && (
        <Card>
          <CardContent className="px-3.5 py-[11px]">
            <div className="flex items-center justify-between">
              <span className="text-[9.5px] font-bold uppercase tracking-[1.1px] text-muted-foreground">Total do Pedido</span>
              <span className="font-display text-[13px] font-bold tabular-nums">
                R${' '}
                {itens
                  .reduce((sum: number, item: any) => sum + (item.subtotal || 0), 0)
                  .toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* BOTÃO FECHAR PEDIDO (COMERCIAL) ou BOTÃO AÇÃO BKO (CONTRATO_PRIME) */}
      {pedido && itens && itens.length > 0 && (
        <>
          {/* Botão para fechar pedido - apenas no quadro COMERCIAL */}
          <BotaoFecharPedido pedido={pedido} />
          
          {/* Botões de ação BKO agora aparecem dentro de cada ItemDetalhadoInline */}
        </>
      )}

      {/* MODAIS */}
      <CriarItemPedidoModal
        open={modalCriarOpen}
        onOpenChange={setModalCriarOpen}
        pedidoId={pedidoId}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['pedido', pedidoId] });
          queryClient.invalidateQueries({ queryKey: ['pedido-itens-completo', pedidoId] });
        }}
      />

      <EditarItemCompletoModal
        open={modalEditarOpen}
        onOpenChange={setModalEditarOpen}
        item={itemParaEditar}
        pedidoId={pedidoId}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['pedido', pedidoId] });
          queryClient.invalidateQueries({ queryKey: ['pedido-itens-completo', pedidoId] });
          setItemParaEditar(null);
        }}
      />

      {itemParaCancelar && (
        <CancelarItemModal
          open={modalCancelarOpen}
          onOpenChange={setModalCancelarOpen}
          itemId={itemParaCancelar.id}
          pedidoId={pedidoId}
          itemNome={itemParaCancelar.produto?.nome || 'Item'}
          onSuccess={() => {
            // ✅ Force refetch imediato após cancelamento
            queryClient.invalidateQueries({ 
              queryKey: ['pedido-itens-completo', pedidoId],
              refetchType: 'active'
            });
            queryClient.invalidateQueries({ 
              queryKey: ['pedidos'],
              refetchType: 'active'
            });
            setItemParaCancelar(null);
          }}
        />
      )}

    </div>
  );
}
